/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import * as React from 'react';
import clsx from 'clsx';
import {sprig} from '@sprig-technologies/sprig-browser';

import {ModalReact} from 'common/components/modal';
import {
  getImmersiveOnboardingSetupGuideInfo,
  ImmersiveOnboardingResult,
} from 'common/report/queries/app-api/immersive-onboarding';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {trackHotjarEvent} from 'common/util/hotjar';
import type {ChromeHeader} from 'common/widgets/chrome/chrome-header';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';

import {useImmer, useMountEffect} from '../utils';
import styles from './index.module.styl';
import {SideNav} from './components';
import {CompleteStep, SetupGuideContext, Step, VisibilityContext} from './types';
import {Events, Explore, Implement, Welcome} from './steps';
import {Scrollable, ScrollableElement, scrollSpy, scrollToStep} from './components/scroll-manager';
import {track, TrackingEvent, TrackingProperty} from './tracking';
import {SPRIG_QUALIFIER} from './constants';

const Sprig = sprig.configure({
  environmentId: `9sZai6GO-WSS`,
});

export const ImmersiveOnboarding = () => {
  const [open, setOpen] = React.useState(true);
  const [step, setStep] = React.useState<Step>(null);
  const [setupGuideInfo, setSetupGuideInfo] = React.useState<ImmersiveOnboardingResult>(null);
  const [isIntegrated, setIsIntegrated] = React.useState(false);
  const [visibility, setVisibility] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);

  useMountEffect(() => {
    const callback = () => {
      setOpen(true);
    };

    document.addEventListener(`onImmersiveOnboardingInit`, callback);

    return () => {
      document.removeEventListener(`onImmersiveOnboardingInit`, callback);
    };
  });

  useMountEffect(() => {
    // Initialize hotjar before loading is done to collect data on loading status
    trackHotjarEvent(`Immersive Onboarding`);

    setIsLoading(true);

    const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
    const projectId = chromeHeader.state.projectId;
    getImmersiveOnboardingSetupGuideInfo(projectId).then((setupGuideInfo) => {
      setSetupGuideInfo(setupGuideInfo);

      const searchParams = new URLSearchParams(window.location.search);

      // Regardless of setup guide status, show Welcome step on their first visit
      // This is working under the assumption that the first load of immersive modal
      // is driven by "immersive-onboarding" query parameter.
      if (searchParams.has(`immersive-onboarding`)) {
        if (searchParams.get(`immersive-onboarding`) === Step.Implement) {
          setStep(Step.Implement);
        } else {
          setStep(Step.Welcome);
        }
      } else {
        if (setupGuideInfo?.steps_completed.length > 0) {
          if (isIntegrated) {
            setStep(Step.Explore);
          } else if (setupGuideInfo.steps_completed.includes(Step.Implement)) {
            if (!isIntegrated) setIsIntegrated(true);
            setStep(Step.Explore);
          } else if (setupGuideInfo.steps_completed.includes(Step.Events)) {
            setStep(Step.Implement);
          } else if (setupGuideInfo.steps_completed.includes(Step.Welcome)) {
            setStep(Step.Events);
          } else {
            setStep(Step.Welcome);
          }
        }
      }

      setIsLoading(false);
    });

    track(TrackingEvent.ModalLoaded);
  });

  React.useEffect(() => {
    if (!isLoading && !isIntegrated && step === Step.Implement) {
      Sprig(`identifyAndTrack`, {
        eventName: `OnboardingFirstImpression`,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id.toString(),
        properties: {variant: `test`},
        showSurveyCallback: async () => {
          window.localStorage.setItem(SPRIG_QUALIFIER, `true`);
          return Promise.resolve(true);
        },
      });
    }
  }, [isIntegrated, isLoading, step]);

  const [darkMode, setDarkMode] = React.useState(false);
  useMountEffect(() => {
    const isDarkMode = document.documentElement.classList.contains(`dark-mode`);
    setDarkMode(isDarkMode);
  });

  const [completed, setCompleted] = useImmer<CompleteStep>({
    [Step.Welcome]: false,
    [Step.Events]: false,
    [Step.Implement]: false,
  });

  React.useEffect(() => {
    if (setupGuideInfo) {
      setCompleted((draft) => {
        draft.welcome = setupGuideInfo.steps_completed.includes(Step.Welcome);
        draft.events = setupGuideInfo.steps_completed.includes(Step.Events);
        draft.implement = setupGuideInfo.steps_completed.includes(Step.Implement);
      });
    }
  }, [setupGuideInfo]);

  const isActive = (stepId) => {
    const orderedStep = Object.values(Step);
    return orderedStep.indexOf(stepId) <= orderedStep.indexOf(step);
  };

  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (initialized.current === false && step !== null) {
      scrollToStep(step, {skipAnimation: true});
      scrollSpy.update();
      initialized.current = true;
    }
  }, [step]);

  const openModal = React.useCallback(() => {
    setOpen(true);
  }, []);

  const closeModal = React.useCallback(() => {
    setOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete(`immersive-onboarding`); // remove query params to prevent immersive onboarding from showing up on app change
    window.history.pushState({}, ``, url);
    // TODO: once React Router is used everywhere, we'll be able to use that
    document.querySelector<ChromeHeader>(`mp-chrome-header`).getContext(`urlManager`).setLocation(window.location.href);
    track(TrackingEvent.SkipForNow, {
      [TrackingProperty.CurrentStep]: step,
    });
  }, [step]);

  return (
    <VisibilityContext.Provider value={{visibility, setVisibility}}>
      <ModalReact open={open} grow-to-content style={{visibility: visibility ? `visible` : `collapse`}}>
        <SetupGuideContext.Provider
          value={{
            modalManager: {openModal, closeModal},
            step,
            setStep,
            setupGuideInfo,
            setSetupGuideInfo,
            isIntegrated,
            setIsIntegrated,
            completed,
          }}
        >
          <div className={clsx(styles.layout, {[styles.darkMode]: darkMode})}>
            <SideNav
              onClose={() => {
                closeModal();
              }}
            />
            <Scrollable className={styles.rhs}>
              {isLoading ? (
                <div className={styles.spinner}>
                  <SpinnerReact size={SpinnerSize.Large} />
                </div>
              ) : (
                <>
                  <ScrollableElement name={Step.Welcome} active={isActive(Step.Welcome)}>
                    <Welcome />
                  </ScrollableElement>
                  <ScrollableElement name={Step.Events} active={isActive(Step.Events)}>
                    <Events />
                  </ScrollableElement>
                  <ScrollableElement name={Step.Implement} active={isActive(Step.Implement)}>
                    <Implement />
                  </ScrollableElement>
                  <ScrollableElement name={Step.Explore} active={isActive(Step.Explore)}>
                    <Explore />
                  </ScrollableElement>
                </>
              )}
            </Scrollable>
          </div>
        </SetupGuideContext.Provider>
      </ModalReact>
    </VisibilityContext.Provider>
  );
};
